import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, useAuth } from '@shop/api-client'
import type { OpeningSession, Warehouse } from '../api/types'
import { Perm } from '../api/types'
import { formatMoment } from '../ui/format'
import { Banner, Button, Empty, ErrorBanner, Field, Panel, Row, Select, Spinner, TextInput } from '@shop/ui'

/**
 * فترةُ الرصيد الافتتاحيّ — <b>وهي السببُ في أنّ الافتتاحَ لا يبقى بابًا مفتوحًا</b>.
 *
 * حركةُ الافتتاح لا تُقبل إلا وفترةُ مستودعها `OPEN`. وبعد الإغلاق يدخل الرصيدُ
 * بمستندٍ (شراءٌ أو استلامٌ أو تسوية) لا بإملاءٍ يقول «عندي عشرة». وإعادةُ الفتح
 * ممكنةٌ بصلاحيّةٍ مستقلّةٍ وسببٍ مكتوب — لأنّها تفتح ذلك البابَ من جديد.
 *
 * وهذه الشاشةُ في النسخة الأولى لسببٍ عمليّ: بندُ «تأكّد أنّ فترةَ المستودع
 * مفتوحة» في قائمة الإصدار يُفحص من هنا بالعين، لا بسؤال قاعدة البيانات.
 */
export default function Opening() {
  const queries = useQueryClient()
  const { can } = useAuth()
  const [warehouseId, setWarehouseId] = useState('')
  const [closing, setClosing] = useState<string | null>(null)
  const [reopening, setReopening] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<unknown>(null)

  const sessions = useQuery({
    queryKey: ['opening'],
    queryFn: () => api<OpeningSession[]>('/api/v1/inventory/opening'),
  })

  const warehouses = useQuery({
    queryKey: ['warehouses', 'all'],
    queryFn: () => api<Warehouse[]>('/api/v1/warehouses'),
    enabled: can(Perm.warehouseView),
  })

  const done = () => {
    setError(null)
    setClosing(null)
    setReopening(null)
    setReason('')
    void queries.invalidateQueries({ queryKey: ['opening'] })
  }

  const open = useMutation({
    mutationFn: () => api<OpeningSession>('/api/v1/inventory/opening', { method: 'POST', body: { warehouseId } }),
    onSuccess: done,
    onError: setError,
  })

  /**
   * ⚠ **وهذا الزرُّ كان ناقصًا — والنقطةُ موجودةٌ في الخادم منذ يومها.**
   *
   *   فكان النظامُ يقول للمالك «إعادةُ الفتح إجراءٌ مستقلٌّ بسببٍ مكتوب» ثمّ
   *   لا يُعطيه مكانًا يكتبه فيه: **طريقٌ مسدودٌ في منتصف إدخال البضاعة**.
   *   ورسالةٌ تشرح ما لا يستطيع المرءُ فعلَه أسوأُ من رسالةٍ لا تشرح.
   */
  const reopen = useMutation({
    mutationFn: (warehouseId: string) =>
      api<OpeningSession>('/api/v1/inventory/opening/reopen', {
        method: 'POST',
        body: { warehouseId, reason: reason.trim() },
      }),
    onSuccess: done,
    onError: setError,
  })

  const close = useMutation({
    mutationFn: (id: string) =>
      api<OpeningSession>(`/api/v1/inventory/opening/${id}/close`, {
        method: 'POST',
        body: { reason: reason.trim() },
      }),
    onSuccess: done,
    onError: setError,
  })

  const names = new Map((warehouses.data ?? []).map((w) => [w.id, w.nameAr]))
  const openOnes = (sessions.data ?? []).filter((s) => s.status === 'OPEN')

  return (
    <>
      <Panel title="فتراتُ الرصيد الافتتاحيّ">
        {sessions.isPending && <Spinner />}
        {sessions.isError && <ErrorBanner error={sessions.error} />}

        {sessions.data?.length === 0 && (
          <Empty>لا فترةَ افتتاحٍ في مستودعاتك — ولا حركةَ افتتاحٍ تُقبل قبل فتحِ واحدة.</Empty>
        )}

        {sessions.data && sessions.data.length > 0 && (
          <Banner tone={openOnes.length > 0 ? 'ok' : 'warn'}>
            {openOnes.length > 0
              ? `مفتوحةٌ الآن: ${openOnes.length} من ${sessions.data.length}. الاعتمادُ بكميّةٍ يعمل في هذه وحدَها.`
              : 'لا فترةَ مفتوحة — كلُّ اعتمادٍ بكميّةٍ سيُردّ.'}
          </Banner>
        )}

        <ul className="mt-3 space-y-2">
          {(sessions.data ?? []).map((session) => (
            <li key={session.id} className="rounded-md border border-line p-3">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium">
                  {names.get(session.warehouseId) ?? 'مستودع'}
                </span>
                <span className={`text-xs ${session.status === 'OPEN' ? 'text-ok' : 'text-ink-soft'}`}>
                  {session.status === 'OPEN' ? 'مفتوحة' : 'مغلقة'}
                </span>
              </div>

              <Row label="فُتحت">{formatMoment(session.openedAt)}</Row>
              <Row label="الحركات">
                <span className="tabular">{session.movementCount}</span>
              </Row>
              {session.closedAt && <Row label="أُغلقت">{formatMoment(session.closedAt)}</Row>}
              {session.closeReason && <Row label="سببُ الإغلاق">{session.closeReason}</Row>}
              {session.reopenReason && <Row label="سببُ إعادة الفتح">{session.reopenReason}</Row>}

              {session.status !== 'OPEN' && can(Perm.openingReopen) && (
                <div className="mt-3">
                  {reopening === session.warehouseId ? (
                    <div className="space-y-2">
                      <Banner tone="warn">
                        إعادةُ الفتح تُتيح إدخالَ رصيدٍ بكميّةٍ بلا مستند. والسببُ
                        يبقى في الدفتر إلى جانب سببِ الإغلاق.
                      </Banner>
                      <Field
                        label="سببُ إعادة الفتح"
                        hint="عشرةُ محارفَ فأكثر — «نقصت أصنافٌ من الجرد الأوّل» تُجيب، و«تصحيح» لا تُجيب."
                      >
                        <TextInput value={reason} onChange={setReason} />
                      </Field>
                      <div className="flex gap-2">
                        <Button
                          tone="primary"
                          disabled={reopen.isPending || reason.trim().length < 10}
                          onClick={() => reopen.mutate(session.warehouseId)}
                        >
                          {reopen.isPending ? 'يُفتح…' : 'تأكيدُ إعادة الفتح'}
                        </Button>
                        <Button onClick={() => setReopening(null)}>تراجع</Button>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={() => { setReopening(session.warehouseId); setReason('') }}>
                      أعِد فتحَ الفترة
                    </Button>
                  )}
                </div>
              )}

              {session.status === 'OPEN' && can(Perm.openingManage) && (
                <div className="mt-3">
                  {closing === session.warehouseId ? (
                    <div className="space-y-2">
                      <Field
                        label="سببُ الإغلاق"
                        hint="عشرةُ محارفَ فأكثر — «تصحيح» لا تُجيب عن شيءٍ يوم يُسأل."
                      >
                        <TextInput value={reason} onChange={setReason} />
                      </Field>
                      <div className="flex gap-2">
                        <Button
                          tone="danger"
                          disabled={close.isPending || reason.trim().length < 10}
                          onClick={() => close.mutate(session.warehouseId)}
                        >
                          تأكيدُ الإغلاق
                        </Button>
                        <Button onClick={() => setClosing(null)}>تراجع</Button>
                      </div>
                    </div>
                  ) : (
                    <Button onClick={() => setClosing(session.warehouseId)}>إغلاقُ الفترة</Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </Panel>

      {can(Perm.openingManage) && (
        <Panel title="افتح فترةً لمستودع">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="المستودع">
              <Select value={warehouseId} onChange={setWarehouseId} disabled={open.isPending}>
                <option value="">اختر مستودعًا…</option>
                {(warehouses.data ?? []).filter((w) => w.isActive).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.nameAr}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="mt-3">
            <Button tone="primary" disabled={open.isPending || warehouseId === ''} onClick={() => open.mutate()}>
              {open.isPending ? 'يُفتح…' : 'افتح الفترة'}
            </Button>
          </div>
        </Panel>
      )}

      <ErrorBanner error={error} />
    </>
  )
}
